import { useMemo, type ComponentType, type ReactNode } from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  Info,
  TrendingUp,
  UserX,
} from "lucide-react";
import { InvestorProgressCharts } from "@/components/admin/InvestorProgressCharts";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  combineInvestorRevenueUsd,
  formatDecimal,
  formatInvestorMoney,
  formatPctChangeLabel,
  formatPeriodRange,
  getCurrentMeasurementPeriod,
  getPeriodStatusFromRange,
  getRecentWeeklyRevenue,
  ARG_REPORTING_TZ,
  INVESTOR_ARS_PER_USD,
  INVESTOR_TRACKING_LABEL,
  INVESTOR_TRACKING_START,
  isFirstTrackedWeek,
  PCT_CHANGE_CAP,
  periodTotalsDifferFromTracking,
  safeNumber,
  sumReportingPeriodTotals,
  type DailyVolumeRow,
  type InvestorTrackingTotals,
  type ReportingPeriodRevenueRow,
  type SubscriptionChurnPeriodRow,
  type WeeklyRevenueRow,
} from "@/lib/investorKpiDisplay";
import { cn } from "@/lib/utils";

export function formatCompactNumber(num: unknown): string {
  const n = safeNumber(num, 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(n);
}

export function formatCompactMoney(amount: unknown, currency: "USD" | "ARS"): string {
  const n = safeNumber(amount, 0);
  const symbol = currency === "USD" ? "$" : "ARS $";
  if (n >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${symbol}${(n / 1_000).toFixed(1)}K`;
  if (currency === "USD") return `${symbol}${n.toFixed(2)}`;
  return `${symbol}${new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(n)}`;
}

export function PctChangeBadge({
  pct,
  isBaseline = false,
  label,
  variant = "inline",
  invertColors = false,
}: {
  pct: number | null;
  isBaseline?: boolean;
  label?: string;
  variant?: "inline" | "pill";
  /** Para métricas donde subir es malo (ej. churn): invierte verde/rojo */
  invertColors?: boolean;
}) {
  if (isBaseline) {
    return (
      <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
        Base
      </Badge>
    );
  }
  if (pct === null) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  const capped = Math.abs(pct) > PCT_CHANGE_CAP;
  const display = formatPctChangeLabel(pct);
  const isPositiveGood = invertColors ? pct < 0 : pct > 0;
  const isNegativeBad = invertColors ? pct > 0 : pct < 0;
  const pillTone =
    isPositiveGood
      ? "text-emerald-700 dark:text-emerald-400 bg-emerald-500/10"
      : isNegativeBad
        ? "text-rose-700 dark:text-rose-400 bg-rose-500/10"
        : "text-muted-foreground bg-muted/50";
  const inlineTone =
    isPositiveGood
      ? "text-emerald-600 dark:text-emerald-400"
      : isNegativeBad
        ? "text-rose-600 dark:text-rose-400"
        : "text-muted-foreground";

  const content =
    variant === "pill" ? (
      <Badge
        variant="outline"
        className={cn("h-5 border-0 px-1.5 text-[10px] font-semibold tabular-nums gap-0.5", pillTone)}
      >
        {pct > 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : pct < 0 ? <ArrowDownRight className="w-2.5 h-2.5" /> : null}
        {display}
      </Badge>
    ) : (
      <span className={cn("inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums", inlineTone)}>
        {pct > 0 ? <ArrowUpRight className="w-3 h-3" /> : pct < 0 ? <ArrowDownRight className="w-3 h-3" /> : null}
        {label ? <span className="font-normal text-muted-foreground">{label}</span> : null}
        {display}
      </span>
    );

  if (!capped) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex">{content}</button>
      </TooltipTrigger>
      <TooltipContent className="text-xs tabular-nums">
        Valor real: {pct > 0 ? "+" : ""}{pct}%
      </TooltipContent>
    </Tooltip>
  );
}

export function InvestorSectionHeading({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="dashboard-section-heading">
      <div className="min-w-0 space-y-0.5">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon className="h-5 w-5 shrink-0 text-primary" />
          {title}
        </h2>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  );
}

export function InvestorMetricCard({
  accent,
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  accent: "green" | "blue" | "purple" | "orange";
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const accentMap = {
    green: {
      border: "border-l-emerald-500 hover:border-l-emerald-500",
      iconBg: "bg-emerald-500/15",
      icon: "text-emerald-600 dark:text-emerald-400",
    },
    blue: {
      border: "border-l-blue-500 hover:border-l-blue-500",
      iconBg: "bg-blue-500/15",
      icon: "text-blue-600 dark:text-blue-400",
    },
    purple: {
      border: "border-l-violet-500 hover:border-l-violet-500",
      iconBg: "bg-violet-500/15",
      icon: "text-violet-600 dark:text-violet-400",
    },
    orange: {
      border: "border-l-amber-500 hover:border-l-amber-500",
      iconBg: "bg-amber-500/15",
      icon: "text-amber-600 dark:text-amber-400",
    },
  } as const;
  const a = accentMap[accent];

  return (
    <div
      className={cn(
        "glass-card rounded-xl border-l-4 p-4 flex flex-col gap-3 transition-all duration-200 hover:border-white/[0.14] hover:bg-white/[0.06]",
        a.border,
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", a.iconBg)}>
          <Icon className={cn("h-4 w-4", a.icon)} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-none text-foreground">{title}</p>
          {subtitle ? (
            <p className="mt-1 text-[10px] text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

function InvestorChurnByPeriodPanel({
  churnByPeriod,
}: {
  churnByPeriod: SubscriptionChurnPeriodRow[];
}) {
  if (churnByPeriod.length === 0) return null;

  const latestWithData = [...churnByPeriod].reverse().find(
    (p) =>
      p.active_at_start > 0 ||
      p.new_subscriptions > 0 ||
      p.lost_from_previous_period > 0,
  );

  return (
    <Card className="glass-card border-border/50 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <UserX className="h-4 w-4 text-rose-500" />
          Churn de suscripciones · mes a mes
        </CardTitle>
        <CardDescription className="text-[11px]">
          Altas = primer cobro recurrente · Bajas = dejaron de pagar vs período anterior · Churn período actual
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {latestWithData && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wider text-rose-600 dark:text-rose-400">
                Churn período actual
              </p>
              <p className="font-display text-xl font-bold tabular-nums mt-1">
                {formatDecimal(latestWithData.churn_rate_period, 2)}%
              </p>
              <div className="mt-1.5">
                <PctChangeBadge
                  pct={latestWithData.churn_rate_pct_change}
                  invertColors
                  variant="pill"
                />
              </div>
            </div>
            <div className="rounded-lg border border-border/40 bg-muted/20 px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Activas inicio</p>
              <p className="font-display text-xl font-bold tabular-nums mt-1">{latestWithData.active_at_start}</p>
            </div>
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Altas</p>
              <p className="font-display text-xl font-bold tabular-nums mt-1">{latestWithData.new_subscriptions}</p>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">Bajas</p>
              <p className="font-display text-xl font-bold tabular-nums mt-1">
                {latestWithData.lost_from_previous_period}
              </p>
            </div>
          </div>
        )}
        <div className="investor-kpi-table-wrap rounded-lg border border-white/[0.08]">
          <table className="investor-kpi-table min-w-[520px]">
            <thead>
              <tr>
                <th className="text-left">Período</th>
                <th className="text-right">Activas</th>
                <th className="text-right">Altas</th>
                <th className="text-right">Bajas</th>
                <th className="text-right">Neto</th>
                <th className="text-right">Churn</th>
                <th className="text-right">Δ churn</th>
              </tr>
            </thead>
            <tbody>
              {churnByPeriod.map((period, idx) => {
                const status = getPeriodStatusFromRange(period.from, period.to, period.days);
                return (
                  <tr
                    key={period.period_id}
                    className={cn(status.isCurrent && "bg-amber-500/[0.06]")}
                  >
                    <td className="px-3 py-3 align-middle">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold">Período {period.period_id}</span>
                        <span className="text-[10px] text-muted-foreground">{formatPeriodRange(period.from, period.to)}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-xs">
                      {period.active_at_start} → {period.active_at_end}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium text-emerald-600 dark:text-emerald-400">
                      +{period.new_subscriptions}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium text-rose-600 dark:text-rose-400">
                      −{period.lost_from_previous_period}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold">
                      {period.net_subscription_change > 0 ? "+" : ""}
                      {period.net_subscription_change}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-bold">
                      {formatDecimal(period.churn_rate_period, 2)}%
                    </td>
                    <td className="px-3 py-3 text-right align-middle">
                      <div className="flex justify-end">
                        <PctChangeBadge
                          pct={period.churn_rate_pct_change}
                          isBaseline={idx === 0}
                          invertColors
                          variant="pill"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          Churn período = dejaron de pagar ÷ pagadores del período anterior. Cancelaciones formales en BD
          (cancelled_in_period) son referencia secundaria. Δ churn positivo (rojo) = empeoró vs período anterior.
        </p>
      </CardContent>
    </Card>
  );
}

export function InvestorRevenueGrowthPanel({
  reportingPeriodRevenue,
  weeklyRevenue,
  dailyVolume = [],
  subscriptionChurnByPeriod,
  trackingLabel,
  trackingTotals,
  exchangeRate = INVESTOR_ARS_PER_USD,
  showChurn = true,
}: {
  reportingPeriodRevenue: ReportingPeriodRevenueRow[];
  weeklyRevenue: WeeklyRevenueRow[];
  dailyVolume?: DailyVolumeRow[];
  subscriptionChurnByPeriod?: SubscriptionChurnPeriodRow[];
  trackingLabel: string;
  trackingTotals: InvestorTrackingTotals & { total_revenue_combined_usd?: number };
  exchangeRate?: number;
  showChurn?: boolean;
}) {
  const currentPeriod = getCurrentMeasurementPeriod();
  const currentPeriodData = reportingPeriodRevenue.find((p) => p.period_id === currentPeriod.id);
  const periodMeta = currentPeriodData ?? currentPeriod;
  const periodStatus = getPeriodStatusFromRange(periodMeta.from, periodMeta.to, periodMeta.days);

  const periodTotals = useMemo(
    () => sumReportingPeriodTotals(reportingPeriodRevenue),
    [reportingPeriodRevenue],
  );

  const recentWeeks = useMemo(
    () => getRecentWeeklyRevenue(weeklyRevenue, 8),
    [weeklyRevenue],
  );

  const trackingCombinedUsd =
    trackingTotals.total_revenue_combined_usd ??
    combineInvestorRevenueUsd(trackingTotals.total_revenue_usd, trackingTotals.total_revenue_ars);

  if (reportingPeriodRevenue.length === 0 && weeklyRevenue.length === 0 && dailyVolume.length === 0) {
    return (
      <div className="glass-card flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed border-border/60 px-8 py-10 text-center">
        <TrendingUp className="mb-3 h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm font-semibold text-foreground">Sin datos de crecimiento aún</p>
        <p className="mt-1 max-w-sm text-xs text-muted-foreground">
          El revenue se acumula desde el {INVESTOR_TRACKING_LABEL}. Los períodos aparecerán conforme cierren cortes comerciales.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {currentPeriodData && (
        <div className="glass-card rounded-xl border-l-4 border-l-amber-500 p-5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-stretch lg:justify-between">
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
                  <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      Período comercial {periodMeta.id}
                    </p>
                    {periodStatus.isCurrent && (
                      <Badge className="h-5 bg-amber-500/15 text-[10px] font-semibold uppercase tracking-wide text-amber-700 hover:bg-amber-500/15 dark:text-amber-300">
                        En curso
                      </Badge>
                    )}
                    {periodStatus.isClosed && (
                      <Badge variant="secondary" className="h-5 text-[10px] font-semibold uppercase tracking-wide">
                        Cerrado
                      </Badge>
                    )}
                    {periodStatus.isUpcoming && (
                      <Badge variant="outline" className="h-5 text-[10px] font-semibold uppercase tracking-wide">
                        Próximo
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatPeriodRange(periodMeta.from, periodMeta.to, true)}
                    <span className="mx-1.5 text-border">·</span>
                    {periodMeta.days} días
                  </p>
                </div>
              </div>
              {periodStatus.isCurrent && (
                <div className="max-w-md space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Día {periodStatus.elapsedDays} de {periodMeta.days}</span>
                    <span className="font-medium text-foreground tabular-nums">{periodStatus.progressPct}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/60">
                    <div
                      className="h-full rounded-full bg-amber-500 transition-all"
                      style={{ width: `${periodStatus.progressPct}%` }}
                    />
                  </div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                {currentPeriodData.transaction_count} transacciones en este corte · {trackingLabel}
              </p>
            </div>
            <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-3 sm:min-w-[420px] lg:min-w-[480px]">
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-3.5 py-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  USD acumulado
                </p>
                <p className="font-display text-xl font-bold leading-none tabular-nums text-foreground mt-1.5">
                  {formatInvestorMoney(currentPeriodData.revenue_usd, "USD")}
                </p>
                <div className="mt-2 space-y-0.5">
                  <PctChangeBadge pct={currentPeriodData.usd_pct_change} variant="pill" />
                  <p className="text-[9px] text-muted-foreground">
                    vs período anterior{periodStatus.isCurrent ? " (parcial)" : ""}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  ARS acumulado
                </p>
                <p className="font-display text-xl font-bold leading-none tabular-nums text-foreground mt-1.5">
                  {formatInvestorMoney(currentPeriodData.revenue_ars, "ARS")}
                </p>
                <div className="mt-2 space-y-0.5">
                  <PctChangeBadge pct={currentPeriodData.ars_pct_change} variant="pill" />
                  <p className="text-[9px] text-muted-foreground">
                    vs período anterior{periodStatus.isCurrent ? " (parcial)" : ""}
                  </p>
                </div>
              </div>
              <div className="rounded-lg border border-violet-500/25 bg-violet-500/5 px-3.5 py-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-violet-600 dark:text-violet-400">
                  Total USD equiv.
                </p>
                <p className="font-display text-xl font-bold leading-none tabular-nums text-foreground mt-1.5">
                  {formatInvestorMoney(currentPeriodData.revenue_combined_usd, "USD")}
                </p>
                <div className="mt-2 space-y-0.5">
                  <PctChangeBadge pct={currentPeriodData.combined_pct_change} variant="pill" />
                  <p className="text-[9px] text-muted-foreground">
                    USD + ARS÷{exchangeRate.toLocaleString("es-AR")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvestorProgressCharts
        dailyVolume={dailyVolume}
        reportingPeriodRevenue={reportingPeriodRevenue}
        weeklyRevenue={weeklyRevenue}
        subscriptionChurnByPeriod={subscriptionChurnByPeriod ?? []}
        exchangeRate={exchangeRate}
        trackingCombinedUsd={trackingCombinedUsd}
      />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <ArrowUpRight className="h-3 w-3 text-emerald-600" />
          Variación positiva
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ArrowDownRight className="h-3 w-3 text-rose-600" />
          Variación negativa
        </span>
        <span className="text-border">|</span>
        <span>Períodos: vs corte anterior</span>
        <span>Semanas: vs semana anterior</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
            >
              <Info className="h-3 w-3" />
              Metodología
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs leading-relaxed">
            North Star: stripe_transactions (scope metadata token) + manual_transactions (columna source).
            Fechas en calendario Argentina ({ARG_REPORTING_TZ}). Total USD equiv. = USD nativo +
            ARS ÷ {exchangeRate.toLocaleString("es-AR")} (tipo de cambio fijo interno).
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {/* Períodos */}
        {reportingPeriodRevenue.length > 0 && (
          <Card className="glass-card border-border/50 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Por período comercial</CardTitle>
              <CardDescription className="text-[11px]">
                Cortes fijos Argentina · variación mes a mes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="investor-kpi-table-wrap rounded-lg border border-white/[0.08]">
                <table className="investor-kpi-table min-w-[520px]">
                  <thead>
                    <tr>
                      <th className="text-left">Período</th>
                      <th className="text-right">USD</th>
                      <th className="text-right">Var.</th>
                      <th className="text-right">ARS</th>
                      <th className="text-right">Var.</th>
                      <th className="text-right">Total USD</th>
                      <th className="text-right">Var.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportingPeriodRevenue.map((period, idx) => {
                      const status = getPeriodStatusFromRange(period.from, period.to, period.days);
                      return (
                        <tr
                          key={period.period_id}
                          className={cn(status.isCurrent && "bg-amber-500/[0.06]")}
                        >
                          <td className="px-3 py-3 align-middle">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-foreground">
                                  Período {period.period_id}
                                </span>
                                {status.isCurrent && (
                                  <Badge variant="outline" className="h-4 px-1 text-[9px] font-medium text-amber-700 dark:text-amber-300">
                                    Actual
                                  </Badge>
                                )}
                              </div>
                              <span className="text-[10px] text-muted-foreground">
                                {formatPeriodRange(period.from, period.to)}
                              </span>
                              <span className="text-[10px] text-muted-foreground tabular-nums">
                                {period.days} días · {period.transaction_count} transacciones
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right align-middle">
                            <span className="font-display text-sm font-bold tabular-nums text-foreground">
                              {formatInvestorMoney(period.revenue_usd, "USD")}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right align-middle">
                            <div className="flex justify-end">
                              <PctChangeBadge pct={period.usd_pct_change} isBaseline={idx === 0} variant="pill" />
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right align-middle">
                            <span className="font-display text-sm font-bold tabular-nums text-foreground">
                              {formatInvestorMoney(period.revenue_ars, "ARS")}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right align-middle">
                            <div className="flex justify-end">
                              <PctChangeBadge pct={period.ars_pct_change} isBaseline={idx === 0} variant="pill" />
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right align-middle">
                            <span className="font-display text-sm font-bold tabular-nums text-violet-700 dark:text-violet-300">
                              {formatInvestorMoney(period.revenue_combined_usd, "USD")}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right align-middle">
                            <div className="flex justify-end">
                              <PctChangeBadge pct={period.combined_pct_change} isBaseline={idx === 0} variant="pill" />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/[0.08] bg-white/[0.03]">
                      <td className="px-3 py-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Σ períodos (P1–P5)
                        </p>
                        <p className="text-[10px] tabular-nums text-muted-foreground">
                          {periodTotals.txs} transacciones
                        </p>
                      </td>
                      <td className="px-3 py-2.5 text-right font-display text-sm font-bold tabular-nums">
                        {formatInvestorMoney(periodTotals.usd, "USD")}
                      </td>
                      <td />
                      <td className="px-3 py-2.5 text-right font-display text-sm font-bold tabular-nums">
                        {formatInvestorMoney(periodTotals.ars, "ARS")}
                      </td>
                      <td />
                      <td className="px-3 py-2.5 text-right font-display text-sm font-bold tabular-nums text-violet-700 dark:text-violet-300">
                        {formatInvestorMoney(periodTotals.combinedUsd, "USD")}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                North Star tracking:{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {formatInvestorMoney(trackingTotals.total_revenue_usd, "USD")}
                </span>
                {" · "}
                <span className="font-medium text-foreground tabular-nums">
                  {formatInvestorMoney(trackingTotals.total_revenue_ars, "ARS")}
                </span>
                {" · "}
                <span className="font-medium text-violet-700 dark:text-violet-300 tabular-nums">
                  {formatInvestorMoney(trackingCombinedUsd, "USD")} total equiv.
                </span>
                {" · "}
                <span className="font-medium text-foreground tabular-nums">
                  {trackingTotals.total_transactions} transacciones
                </span>
                {periodTotalsDifferFromTracking(periodTotals, trackingTotals) ? (
                  <span className="ml-1 text-amber-700 dark:text-amber-400">
                    (Σ períodos puede diferir si hay fechas fuera de cortes P1–P5)
                  </span>
                ) : null}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Semanas */}
        {weeklyRevenue.length > 0 && (
          <Card className="glass-card border-border/50 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Por semana</CardTitle>
              <CardDescription className="text-[11px]">
                Semanas ISO (lunes a domingo) · últimas 8 semanas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                {recentWeeks.map((week, idx) => (
                  <div
                    key={week.week_start}
                    className={cn(
                      "glass-card rounded-lg p-3 transition-colors",
                      week.is_current_week
                        ? "border-amber-500/40 bg-amber-500/[0.04]"
                        : "hover:border-border/60",
                    )}
                  >
                    <div className="mb-2.5 flex items-center justify-between gap-2">
                      <p className="min-w-0 truncate text-xs font-semibold text-foreground">
                        {format(parseISO(week.week_start), "d MMM", { locale: es })} –{" "}
                        {format(parseISO(week.week_end), "d MMM yyyy", { locale: es })}
                      </p>
                      {week.is_current_week ? (
                        <Badge className="h-5 shrink-0 bg-amber-500/15 text-[10px] font-medium text-amber-700 hover:bg-amber-500/15 dark:text-amber-300">
                          En curso
                        </Badge>
                      ) : (
                        <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                          Sem. {idx + 1}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-md border border-blue-500/15 bg-blue-500/5 px-2.5 py-2">
                        <p className="text-[9px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">USD</p>
                        <p className="font-display text-sm font-bold tabular-nums">{formatInvestorMoney(week.revenue_usd, "USD")}</p>
                        <div className="mt-1">
                          <PctChangeBadge
                            pct={week.usd_pct_change}
                            isBaseline={isFirstTrackedWeek(week.week_start, weeklyRevenue)}
                            variant="pill"
                          />
                        </div>
                      </div>
                      <div className="rounded-md border border-emerald-500/15 bg-emerald-500/5 px-2.5 py-2">
                        <p className="text-[9px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">ARS</p>
                        <p className="font-display text-sm font-bold tabular-nums">{formatInvestorMoney(week.revenue_ars, "ARS")}</p>
                        <div className="mt-1">
                          <PctChangeBadge
                            pct={week.ars_pct_change}
                            isBaseline={isFirstTrackedWeek(week.week_start, weeklyRevenue)}
                            variant="pill"
                          />
                        </div>
                      </div>
                      <div className="rounded-md border border-violet-500/15 bg-violet-500/5 px-2.5 py-2">
                        <p className="text-[9px] font-medium uppercase tracking-wider text-violet-600 dark:text-violet-400">Total</p>
                        <p className="font-display text-sm font-bold tabular-nums">{formatInvestorMoney(week.revenue_combined_usd, "USD")}</p>
                        <div className="mt-1">
                          <PctChangeBadge
                            pct={week.combined_pct_change}
                            isBaseline={isFirstTrackedWeek(week.week_start, weeklyRevenue)}
                            variant="pill"
                          />
                        </div>
                      </div>
                    </div>
                    <p className="mt-2 text-[10px] text-muted-foreground tabular-nums">
                      {week.transaction_count} transacciones
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {showChurn && subscriptionChurnByPeriod && subscriptionChurnByPeriod.length > 0 && (
        <InvestorChurnByPeriodPanel churnByPeriod={subscriptionChurnByPeriod} />
      )}
    </div>
  );
}
